import { map } from 'lodash';

import { getMomentDateWithoutTimezone } from './datetime';

export const normalizeUserInsurancePolicy = policy => {
  if (!policy) return null;

  const { insuredAddress, insuranceCoverages } = policy;

  const companyNotInList =
    !!policy.insurancePolicyId &&
    !!policy.insurancePolicyName &&
    policy.insurancePolicyId === policy.insurancePolicyName;

  return {
    _id: policy._id,
    status: policy.status,
    outOfCompliance: policy.outOfCompliance,
    effectiveStartDate: getMomentDateWithoutTimezone(policy.effectiveStartDate),
    effectiveEndDate: getMomentDateWithoutTimezone(policy.effectiveEndDate),
    insurancePolicyName: companyNotInList ? 'other' : policy.insurancePolicyId,
    otherInsurancePolicyName: companyNotInList ? policy.insurancePolicyName : undefined,
    policyDocs: policy.policyDocs,
    // Address
    streetOne: insuredAddress?.streetOne,
    streetTwo: insuredAddress?.streetTwo,
    city: insuredAddress?.city,
    state: insuredAddress?.state,
    postalCode: insuredAddress?.postalCode,
    country: insuredAddress?.country || 'US',
    // Coverages
    bodilyInjuryToOthers_person: insuranceCoverages?.bodilyInjuryToOthers?.person,
    bodilyInjuryToOthers_accident: insuranceCoverages?.bodilyInjuryToOthers?.accident,
    damageToProperty_person: insuranceCoverages?.damageToProperty?.person,
    damageToProperty_accident: insuranceCoverages?.damageToProperty?.accident,
    personalInjuryProtection_person: insuranceCoverages?.personalInjuryProtection?.person,
    personalInjuryProtection_accident: insuranceCoverages?.personalInjuryProtection?.accident,
    bodilyInjuryByUninsuredAuto_person: insuranceCoverages?.bodilyInjuryByUninsuredAuto?.person,
    bodilyInjuryByUninsuredAuto_accident: insuranceCoverages?.bodilyInjuryByUninsuredAuto?.accident,
    optionalBodilyInjuryToOthers_person: insuranceCoverages?.optionalBodilyInjuryToOthers?.person,
    optionalBodilyInjuryToOthers_accident:
      insuranceCoverages?.optionalBodilyInjuryToOthers?.accident,
    medicalPayments_person: insuranceCoverages?.medicalPayments?.person,
    medicalPayments_accident: insuranceCoverages?.medicalPayments?.accident,
    collision_person: insuranceCoverages?.collision?.person,
    collision_accident: insuranceCoverages?.collision?.accident,
    comprehensive_person: insuranceCoverages?.comprehensive?.person,
    comprehensive_accident: insuranceCoverages?.comprehensive?.accident,
    bodilyInjuryByUnderinsuredAuto_person:
      insuranceCoverages?.bodilyInjuryByUnderinsuredAuto?.person,
    bodilyInjuryByUnderinsuredAuto_accident:
      insuranceCoverages?.bodilyInjuryByUnderinsuredAuto?.accident,
  };
};

export const setupPolicyDocumentsUploadList = list => {
  if (!Array.isArray(list)) return [];

  return list
    .map(image => ({
      uid: image.fileKey,
      name: image.filename,
      status: 'done',
      timestamp: image.timestamp,
    }))
    .sort((a, b) => {
      if (!a.timestamp || !b.timestamp) return 1;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
};

const normalizeInsuranceAddress = address => ({
  streetOne: address?.streetOne || '',
  streetTwo: address?.streetTwo || '',
  city: address?.city || '',
  state: address?.state || '',
  postalCode: address?.postalCode || '',
  country: address?.country === 'USA' ? 'US' : address?.country || '',
});

const normalizeInsuranceCoverages = coverages => {
  return map(coverages, coverage => {
    const coverageMapping = {
      BI: [
        {
          type: 'bodilyInjuryPerPerson',
          value: { amount: coverage.limitPerPerson },
          valueType: 'AMOUNT',
        },
        {
          type: 'bodilyInjuryPerAccident',
          value: { amount: coverage.limitPerAccident },
          valueType: 'AMOUNT',
        },
      ],
      PI: [], // Personal Injury - Not supported by Axle
      UMBI: [
        {
          type: 'uninsuredMotoristBodilyInjuryPerPerson',
          value: { amount: coverage.limitPerPerson },
          valueType: 'AMOUNT',
        },
        {
          type: 'uninsuredMotoristBodilyInjuryPerAccident',
          value: { amount: coverage.limitPerAccident },
          valueType: 'AMOUNT',
        },
      ],
      UMPD: [
        {
          type: 'uninsuredMotoristPropertyDamagePerPerson',
          value: { amount: coverage.limitPerPerson },
          valueType: 'AMOUNT',
        },
        {
          type: 'uninsuredMotoristPropertyDamagePerAccident',
          value: { amount: coverage.limitPerAccident },
          valueType: 'AMOUNT',
        },
      ],
      PD: [
        {
          type: 'propertyDamage',
          value: { amount: coverage.deductible },
          valueType: 'AMOUNT',
        },
        {
          type: 'propertyDamagePerAccident',
          value: { amount: coverage.limitPerAccident },
          valueType: 'AMOUNT',
        },
      ],
      OBI: [], // Optional Bodily Injury to Others - Not supported by Axle
      MP: [], // Medical Payments - Not supported by Axle
      COLL: [
        {
          type: 'collisionAmount',
          value: { amount: coverage.deductible },
          valueType: 'AMOUNT',
        },
        {
          type: 'collisionLimit',
          value: { amount: coverage.limitPerAccident },
          valueType: 'AMOUNT',
        },
      ],
      COMP: [
        {
          type: 'comprehensiveAmount',
          value: { amount: coverage.deductible },
          valueType: 'AMOUNT',
        },
        {
          type: 'comprehensiveLimit',
          value: { amount: coverage.limitPerAccident },
          valueType: 'AMOUNT',
        },
      ],
      UIMBI: [
        {
          type: 'underinsuredMotoristBodilyInjuryPerPerson',
          value: { amount: coverage.limitPerPerson },
          valueType: 'AMOUNT',
        },
        {
          type: 'underinsuredMotoristBodilyInjuryPerAccident',
          value: { amount: coverage.limitPerAccident },
          valueType: 'AMOUNT',
        },
      ],
      UIMPD: [
        {
          type: 'underinsuredMotoristPropertyDamageAmount',
          value: { amount: coverage.deductible },
          valueType: 'AMOUNT',
        },
        {
          type: 'underinsuredMotoristPropertyDamageLimit',
          value: { amount: coverage.limitPerAccident },
          valueType: 'AMOUNT',
        },
      ],
    };

    return coverageMapping[coverage.code] || [];
  }).flat();
};

export const normalizeAxleInsuranceInformation = data => {
  if (!data) return null;

  const {
    _id,
    policyId,
    axleAccountId,
    axleClientId,
    isSelectedForComplianceCheck,
    policyNumber,
    insureds = [],
    address = {},
    coverages = [],
    effectiveDate,
    expirationDate,
    carrier,
    properties = [],
    createdAt,
    refreshedAt,
    modifiedAt,
  } = data;

  const normalizedAddres = normalizeInsuranceAddress(address);
  const policyHolders = map(insureds, insured => ({
    name: `${insured.firstName} ${insured.lastName}`.trim(),
    address: normalizedAddres,
  }));

  return {
    _id,
    policyId,
    axleAccountId,
    axleClientId,
    address: normalizedAddres,
    isSelectedForComplianceCheck,
    policyNumber,
    policyHolders,
    vehicleInfo: properties,
    createdAt,
    updatedAt: refreshedAt,
    modifiedAt,
    insuranceProvider: carrier,
    coverages: normalizeInsuranceCoverages(coverages),
    coveragePeriod: {
      startDate: effectiveDate,
      endDate: expirationDate,
    },
  };
};

export const normalizeVehicleInsuranceInformation = data => {
  if (!data) return null;

  const {
    _id,
    policyNumber,
    insureds = [],
    insuredAddress = {},
    coverages = [],
    effectiveDate,
    expirationDate,
    carrier,
    properties = [],
    created,
    updated,
    createdAt,
    updatedAt,
    isSelectedForComplianceCheck,
    source = {},
    uploadedFiles = [],
  } = data;

  const normalizedAddres = normalizeInsuranceAddress(insuredAddress);
  const policyHolders = map(insureds, insured => ({
    name: `${insured.firstName} ${insured.lastName}`.trim(),
    address: normalizedAddres,
  }));

  return {
    _id,
    policyNumber,
    isSelectedForComplianceCheck,
    provider: source.provider,
    source,
    externalPolicyId: source.externalPolicyId,
    connection: source.externalAccountId?.connection,
    address: normalizedAddres,
    policyHolders,
    vehicleInfo: properties,
    insuranceProvider: carrier,
    coverages: normalizeInsuranceCoverages(coverages),
    coveragePeriod: {
      startDate: effectiveDate,
      endDate: expirationDate,
    },
    effectiveDate,
    expirationDate,
    uploadedFiles: map(uploadedFiles, file => ({
      _id: file._id,
      name: file.originalFilename,
      mimeType: file.mimeType,
      size: file.fileSizeBytes,
      createdAt: file.created,
    })),
    createdAt: source.createdAt || createdAt || created,
    updatedAt: source.refreshedAt || updatedAt || updated,
    modifiedAt: source.modifiedAt,
  };
};
